import { supabase } from "../supabaseClient";

export const DRUG_MATRIX_CATEGORY_GUIDE = [
  {
    name: "General Anaesthetic",
    description: "Medicines used to cause unconsciousness and loss of awareness during surgery and other medical procedures. They may be given by injection or inhaled and are often used alongside pain relief and muscle relaxants.",
  },
  {
    name: "Local Anaesthetic",
    description: "Medicines used to numb a specific area of the body by temporarily blocking nerve signals. They are commonly used for injections, wound treatment, minor procedures and regional anaesthesia.",
  },
  {
    name: "Hypnotics",
    description: "Medicines used to induce sleep, sedation or unconsciousness. They are commonly used for insomnia, procedural sedation and as part of general anaesthesia.",
  },
  {
    name: "Benzodiazepines",
    description: "Medicines used for sedation, anxiolysis, seizure control and management of acute withdrawal states.",
  },
  {
    name: "Opioid Analgesia",
    description: "Medicines used for moderate to severe pain relief that work on opioid receptors in the brain and nervous system.",
  },
  {
    name: "Analgesia",
    description: "Medicines used to relieve pain without being classed as opioid analgesics.",
  },
  {
    name: "Neuromuscular Blockers",
    description: "Neuromuscular blocking agents used to induce skeletal muscle paralysis to facilitate tracheal intubation, mechanical ventilation and surgical procedures.",
  },
  {
    name: "Depolarising Neuromuscular Blockers",
    description: "Medicines used to produce rapid, short-acting skeletal muscle paralysis, primarily to facilitate rapid sequence induction (RSI), tracheal intubation and short emergency procedures.",
  },
  {
    name: "Antiemetics",
    description: "Medicines used to prevent or treat nausea and vomiting.",
  },
  {
    name: "Antisialagogues",
    description: "Medicines used to reduce salivary and respiratory secretions, improve airway management and support anaesthetic and procedural care.",
  },
  {
    name: "IV Fluids Bags",
    description: "Intravenous fluids used to maintain or replace fluid volume, support hydration and electrolyte balance, and where appropriate act as a carrier for compatible IV medicines.",
  },
  {
    name: "Antibiotics",
    description: "Medicines used to prevent or treat bacterial infections.",
  },
  {
    name: "Trauma",
    description: "Medicines and fluids commonly used during the immediate management of traumatic injury and resuscitation within the roleplay setting.",
  },
];

export const DRUG_MATRIX_CATEGORY_NAMES = DRUG_MATRIX_CATEGORY_GUIDE.map((item) => item.name);

export function slugifyDrug(value) {
  return String(value || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

export async function loadDrugMatrix({ includeArchived = false } = {}) {
  const [categoriesResult, labelsResult, drugsResult, linksResult] = await Promise.all([
    supabase.from("drug_matrix_categories").select("*").order("sort_order").order("name"),
    supabase.from("drug_matrix_labels").select("*").order("name"),
    (() => {
      let query = supabase.from("drug_matrix_drugs").select("*").order("sort_order").order("name");
      if (!includeArchived) query = query.eq("archived", false);
      return query;
    })(),
    supabase.from("drug_matrix_drug_labels").select("drug_id,label_id,assigned_at"),
  ]);

  const firstError = [categoriesResult, labelsResult, drugsResult, linksResult].find((result) => result.error)?.error;
  if (firstError) throw firstError;

  const categories = categoriesResult.data || [];
  const labels = labelsResult.data || [];
  const drugs = drugsResult.data || [];
  const links = linksResult.data || [];
  const categoryById = new Map(categories.map((category) => [String(category.id), category]));
  const labelById = new Map(labels.map((label) => [label.id, label]));
  const labelsByDrug = new Map();

  for (const link of links) {
    const label = labelById.get(link.label_id);
    if (!label) continue;
    const list = labelsByDrug.get(link.drug_id) || [];
    list.push(label);
    labelsByDrug.set(link.drug_id, list);
  }

  return {
    categories,
    labels,
    links,
    drugs: drugs.map((drug) => ({
      ...drug,
      category: categoryById.get(String(drug.category_id)) || null,
      labels: (labelsByDrug.get(drug.id) || []).sort((a, b) => a.name.localeCompare(b.name)),
    })),
  };
}
